import React, { useEffect, useState } from 'react'
import { Form, Modal } from 'semantic-ui-react'
import { FlexRowBetween, NumericStepper, TitleText } from 'components'
import { X } from 'phosphor-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { EscalationPolicyService } from 'utility'
import { RecipientPicker } from './RecipientPicker'
import {
    buildPolicyPayload,
    getApiErrorMessage,
    normalizeEscalationPolicy,
} from './utils'
import {
    FormHelper,
    InlineRow,
    ModalError,
    PolicyHint,
    PolicyModalActions,
    ReminderRow,
    SegBtn,
    Segmented,
} from './styles'

const emptyForm = () => ({
    name: '',
    reminderEnabled: true,
    reminderMinutes: 30,
    recipientTypeId: 1,
    recipientId: null,
    vendorCompanyId: null,
    recipientLabel: '',
    isActive: true,
})

export const EscalationPolicyFormModal = ({
    open,
    onClose,
    policy,
    readOnly = false,
    onSaved,
}) => {
    const isEdit = Boolean(policy?.id)
    const [form, setForm] = useState(emptyForm())
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!open) return
        setError('')
        setConfirmDelete(false)
        if (policy) {
            setForm({
                name: policy.name || '',
                reminderEnabled: Boolean(policy.reminderMinutes),
                reminderMinutes: policy.reminderMinutes || 30,
                recipientTypeId: policy.recipientTypeId || 1,
                recipientId: policy.recipientId ?? null,
                vendorCompanyId: policy.vendorCompanyId ?? null,
                recipientLabel: policy.recipientName || '',
                isActive: policy.isActive !== false,
            })
        } else {
            setForm(emptyForm())
        }
    }, [open, policy])

    const updateForm = patch => setForm(prev => ({ ...prev, ...patch }))

    const canSave =
        form.name.trim() &&
        form.recipientId &&
        (!form.reminderEnabled || Number(form.reminderMinutes) > 0)

    const handleSave = async () => {
        if (!canSave || readOnly) return
        setSaving(true)
        setError('')
        try {
            const payload = buildPolicyPayload(form)
            const res = isEdit
                ? await EscalationPolicyService.update(policy.id, payload)
                : await EscalationPolicyService.create(payload)
            const saved = normalizeEscalationPolicy(
                res?.Data ?? res?.data ?? res ?? {},
            )
            if (!saved.id && isEdit) saved.id = policy.id
            // Create often returns only { PolicyId } — keep form fields for UI
            if (!saved.id) {
                saved.id =
                    res?.PolicyId ??
                    res?.EscalationPolicyId ??
                    res?.Id ??
                    res?.id
            }
            if (!saved.name) saved.name = form.name.trim()
            if (!saved.recipientName) saved.recipientName = form.recipientLabel
            if (saved.reminderMinutes == null && form.reminderEnabled) {
                saved.reminderMinutes = Number(form.reminderMinutes) || null
            }
            if (!saved.recipientTypeId) {
                saved.recipientTypeId = Number(form.recipientTypeId) || 1
            }
            if (saved.recipientId == null) {
                saved.recipientId = form.recipientId
            }
            if (saved.isActive == null) saved.isActive = form.isActive !== false
            onSaved?.(saved)
            onClose?.()
        } catch (err) {
            setError(getApiErrorMessage(err))
        } finally {
            setSaving(false)
        }
    }

    const softDeletePolicy = async () => {
        const policyId = Number(policy.id)
        try {
            await EscalationPolicyService.remove(policyId)
        } catch (err) {
            // Prod help lists DELETE, but it currently 404s. Soft-delete via PUT.
            if (err?.status !== 404) throw err
            const payload = {
                ...buildPolicyPayload(form),
                IsActive: false,
            }
            await EscalationPolicyService.update(policyId, payload)
        }
    }

    const handleDelete = async () => {
        if (!isEdit || readOnly) return
        if (!form.isActive) {
            setError(
                'This policy is already Disabled. Soft-delete only flips Active → Disabled and keeps rule linkages.',
            )
            return
        }
        if (!confirmDelete) {
            setConfirmDelete(true)
            return
        }
        setDeleting(true)
        setError('')
        try {
            await softDeletePolicy()
            onSaved?.({
                ...policy,
                ...form,
                name: form.name.trim(),
                recipientName: form.recipientLabel || policy.recipientName,
                isActive: false,
                softDeleted: true,
            })
            onClose?.()
        } catch (err) {
            setError(getApiErrorMessage(err))
            setConfirmDelete(false)
        } finally {
            setDeleting(false)
        }
    }

    const title =
        (readOnly && 'Escalation policy') ||
        (isEdit && 'Edit escalation policy') ||
        'New escalation policy'

    return (
        <Modal open={open} onClose={onClose} size="small">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>{title}</TitleText>
                    <X className="pointer" size={28} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form error={Boolean(error)}>
                    {error && <ModalError>{error}</ModalError>}

                    <Form.Field required>
                        <label>Policy name</label>
                        <Form.Input
                            value={form.name}
                            placeholder="e.g. Facility Manager – Fast"
                            disabled={readOnly}
                            onChange={(_, { value }) =>
                                updateForm({ name: value })
                            }
                        />
                        <FormHelper>
                            Shown in the rule editor&apos;s escalation-policy
                            dropdown.
                        </FormHelper>
                    </Form.Field>

                    <Form.Field>
                        <label>Soft reminder</label>
                        <ReminderRow>
                            <Segmented>
                                <SegBtn
                                    type="button"
                                    $on={form.reminderEnabled}
                                    disabled={readOnly}
                                    onClick={() =>
                                        updateForm({ reminderEnabled: true })
                                    }
                                >
                                    Enabled
                                </SegBtn>
                                <SegBtn
                                    type="button"
                                    $on={!form.reminderEnabled}
                                    disabled={readOnly}
                                    onClick={() =>
                                        updateForm({ reminderEnabled: false })
                                    }
                                >
                                    Off
                                </SegBtn>
                            </Segmented>
                            {form.reminderEnabled && (
                                <>
                                    <NumericStepper
                                        min={1}
                                        step={1}
                                        value={
                                            Number(form.reminderMinutes) || 1
                                        }
                                        disabled={readOnly}
                                        onChange={value =>
                                            updateForm({
                                                reminderMinutes: value,
                                            })
                                        }
                                    />
                                    <span className="minutes-label">
                                        minutes after ticket creation
                                    </span>
                                </>
                            )}
                        </ReminderRow>
                        <PolicyHint>
                            If the assignee doesn&apos;t acknowledge within this
                            window, send a reminder email + push to the{' '}
                            <b>same assignee</b>. Fires once per ticket. Leave
                            Off to skip this stage — escalation still fires at
                            the ack SLA.
                        </PolicyHint>
                    </Form.Field>

                    <RecipientPicker
                        typeId={form.recipientTypeId}
                        recipientId={form.recipientId}
                        vendorCompanyId={form.vendorCompanyId}
                        recipientLabel={form.recipientLabel}
                        disabled={readOnly}
                        help="When the ack SLA elapses without acknowledgement, notify this recipient. Pick one User, Team, Vendor Company, or Vendor Contact."
                        onChange={({
                            typeId,
                            recipientId,
                            vendorCompanyId,
                            label,
                        }) =>
                            updateForm({
                                recipientTypeId: typeId,
                                recipientId,
                                vendorCompanyId,
                                recipientLabel: label || '',
                            })
                        }
                    />
                    <PolicyHint>
                        Vendor recipients get email only. Internal User + Team
                        recipients get email + push if they have a registered
                        mobile device.
                    </PolicyHint>

                    <Form.Field>
                        <label>SLA breach behaviour</label>
                        <PolicyHint>
                            When the resolve SLA elapses without resolution,
                            notify the same escalation recipient and mark the
                            ticket overdue. This stage is always on. If you
                            don&apos;t want it, remove the resolve SLA on the
                            linked rule.
                        </PolicyHint>
                    </Form.Field>

                    <Form.Field>
                        <label>Status</label>
                        <InlineRow>
                            <Segmented>
                                <SegBtn
                                    type="button"
                                    $on={form.isActive}
                                    disabled={readOnly}
                                    onClick={() =>
                                        updateForm({ isActive: true })
                                    }
                                >
                                    Active
                                </SegBtn>
                                <SegBtn
                                    type="button"
                                    $on={!form.isActive}
                                    disabled={readOnly}
                                    onClick={() =>
                                        updateForm({ isActive: false })
                                    }
                                >
                                    Disabled
                                </SegBtn>
                            </Segmented>
                            <span style={{ color: '#6b7280', fontSize: 12 }}>
                                Disabling stops future sweeps from firing but
                                keeps rule linkages intact.
                            </span>
                        </InlineRow>
                        <FormHelper style={{ marginTop: 8 }}>
                            <b>Delete policy</b> is a soft-delete: it flips
                            Status to Disabled and keeps rule linkages (same as
                            the backend map). It does not remove the row.
                        </FormHelper>
                    </Form.Field>
                </Form>
            </Modal.Content>
            <PolicyModalActions $end>
                {isEdit && !readOnly && form.isActive && (
                    <AppSecondaryButton
                        onClick={handleDelete}
                        disabled={saving || deleting}
                        style={{ color: '#b91c1c' }}
                    >
                        {confirmDelete
                            ? 'Confirm soft-delete?'
                            : 'Delete policy'}
                    </AppSecondaryButton>
                )}
                <AppSecondaryButton
                    onClick={onClose}
                    disabled={saving || deleting}
                >
                    {readOnly ? 'Close' : 'Cancel'}
                </AppSecondaryButton>
                {!readOnly && (
                    <AppPrimaryButton
                        onClick={handleSave}
                        loading={saving}
                        disabled={saving || deleting || !canSave}
                    >
                        {isEdit ? 'Save changes' : 'Create policy'}
                    </AppPrimaryButton>
                )}
            </PolicyModalActions>
        </Modal>
    )
}
